import { createContext, useCallback, useContext, useEffect, useState,
         type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { auth, NotSignedIn, onSignedOut } from '../api/client'
import type { AuthUser, Role } from '../api/types'
import { WorkspaceReady } from '../components/WorkspaceReady'
import { calculatedSensorStore } from './calculatedSensors'
import { manualSensorStore } from './manualSensors'
import { strategyStore } from './strategies'
import { flushDocuments, forgetDocuments } from './userDocuments'

export type Route =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'forbidden' }
  | { kind: 'redirect'; to: string }

// Overview replaced the Stations page as home (owner, 2026-10-22).
const HOME = '/overview'

/** Where a reader with this role may be, as a pure function of the two.
 *
 *  `undefined` is "still asking /api/auth/me" and renders nothing — never the
 *  previous user's screen, the same rule ChartFrame follows for a chart. */
export function routeFor(role: Role | null | undefined, path: string): Route {
  if (role === undefined) return { kind: 'wait' }
  const bare = path.split('?')[0] ?? path
  if (role === null || role === 'disabled') {
    return bare === '/login'
      ? { kind: 'render' }
      : { kind: 'redirect', to: `/login?return_to=${encodeURIComponent(path)}` }
  }
  if (role === 'pending') {
    return bare === '/pending' ? { kind: 'render' } : { kind: 'redirect', to: '/pending' }
  }
  if (bare === '/login' || bare === '/pending') return { kind: 'redirect', to: HOME }
  if (bare.startsWith('/admin') && role !== 'admin') return { kind: 'forbidden' }
  // Creating a project is a platform admin's. Editing one is open here: the
  // page reads the reader's role in that project, and the server decides.
  if (bare === '/projects/new' && role !== 'admin') return { kind: 'forbidden' }
  return { kind: 'render' }
}

/** What the No access page says: what the refused page needs. */
export function forbiddenMessage(path: string): string {
  if ((path.split('?')[0] ?? path) === '/projects/new') {
    return 'Creating a project needs the platform admin role. Ask an administrator if you need one.'
  }
  return 'The admin panel needs the admin role. Ask an administrator if you need it.'
}

export interface NavItem {
  to: string
  label: string
  /** A menu instead of a link (Sensors, phase 6). */
  menu?: { to: string; label: string; hint: string }[]
  /** Paths under which the item reads as current. */
  match?: string[]
}

// Named as the redesign names them (spec §3); the routes are unchanged.
// Sensors opens a menu since phase 6: a station's readings stay at
// /explore/:code ("Station readings", which the board's menu left out and
// nothing else in the bar reaches), beside Sensor info and Explore sensors.
const NAV: NavItem[] = [
  { to: '/stations', label: 'Home' },
  {
    to: '/explore', label: 'Sensors', match: ['/explore', '/sensors'],
    menu: [
      { to: '/explore', label: 'Station readings', hint: "One station's readings, charted and exported" },
      { to: '/sensors/info', label: 'Sensor info', hint: "Each sensor's properties and place on the map" },
      { to: '/sensors/compare', label: 'Compare sensors',
        hint: 'Sensors from several stations on one chart' },
    ],
  },
  { to: '/map', label: 'Map' },
  { to: '/dashboards', label: 'Dashboards' },
  { to: '/projects', label: 'Projects' },
]

const ALERTS: NavItem = { to: '/alerts', label: 'Alerts' }

/** The Admin and Intake tabs exist only for admins — hidden, not greyed out.
 *  Alerts (phase 4) shows to admins and to anyone who manages a project (its
 *  Editors and Admins set rules on its stations). */
export function navItems(role: Role | null | undefined, managesProjects = false): NavItem[] {
  if (role === 'admin') {
    return [...NAV, ALERTS, { to: '/admin', label: 'Admin' }, { to: '/admin/intake', label: 'Intake' }]
  }
  if (role !== 'viewer') return []
  return managesProjects ? [...NAV, ALERTS] : NAV
}

/** A page navigation, never a fetch: the flow is a chain of top-level
 *  redirects through Microsoft and back. `chooseAccount` opens Microsoft's
 *  account picker, which otherwise signs the browser straight back in as
 *  whoever it last saw. */
export function loginHref(returnTo: string, chooseAccount = false): string {
  const href = `/api/auth/login?return_to=${encodeURIComponent(returnTo)}`
  return chooseAccount ? `${href}&account=choose` : href
}

interface AuthState {
  user: AuthUser | null | undefined
  /** Why the last session ended, if it ended under the reader. */
  reason?: string
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState>({
  user: undefined, signOut: async () => {},
})

export function useAuth(): AuthState {
  return useContext(AuthContext)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined)
  const [reason, setReason] = useState<string>()

  useEffect(() => {
    auth.me().then(setUser, (cause: unknown) => {
      // A 401 here is simply "not signed in", not a session that ended under
      // the reader — so clear the reason the signed-out event (which fires
      // first) just set. Anything else is an outage, which the sign-in page
      // reports rather than looping on.
      setUser(null)
      setReason(cause instanceof NotSignedIn ? undefined : String(cause))
    })
  }, [])

  // Nothing of one person's may show for the next: the lists and documents
  // held for the workspace are dropped whenever nobody is signed in.
  useEffect(() => {
    if (user !== null) return
    forgetDocuments(); manualSensorStore.reset(); calculatedSensorStore.reset(); strategyStore.reset()
  }, [user])

  // Any request, anywhere, that meets a lapsed session.
  useEffect(() => onSignedOut((why) => {
    setUser(null)
    setReason(why)
  }), [])

  const signOut = useCallback(async () => {
    // What is waiting to be saved goes while the session still holds.
    await flushDocuments()
    try {
      await auth.logout()
    } catch {
      // Signing out locally is the point of this call, and a request that
      // fails here — a session already lapsed (401) or a network error —
      // means the server already has no session to hold, or is unreachable
      // either way. Swallow it so callers doing `void signOut()` never see
      // an unhandled rejection; the local state is cleared below regardless.
    } finally {
      setReason(undefined)
      setUser(null)
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user, reason, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

/** Renders its children only where routeFor allows. */
export function Gate({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const location = useLocation()
  const route = routeFor(user === undefined ? undefined : user?.role ?? null,
                         location.pathname + location.search)
  if (route.kind === 'wait') return <div className="pad muted">Checking your session…</div>
  if (route.kind === 'redirect') return <Navigate to={route.to} replace />
  if (route.kind === 'forbidden') {
    return (
      <div className="pad">
        <h1>No access</h1>
        <p className="muted">{forbiddenMessage(location.pathname)}</p>
      </div>
    )
  }
  // A signed-in reader's workspace documents load before any page reads
  // them (components/WorkspaceReady.tsx); the sign-in and waiting pages
  // have none.
  if (user && user.role !== 'pending' && user.role !== 'disabled') return <WorkspaceReady>{children}</WorkspaceReady>
  return <>{children}</>
}
